/**
 * ATS Compliance Analyzer
 * Provides comprehensive ATS compatibility scoring and recommendations
 */

export interface ATSComplianceReport {
  overallScore: number;
  sections: {
    formatting: number;
    keywords: number;
    structure: number;
    content: number;
  };
  recommendations: string[];
  warnings: string[];
  strengths: string[];
}

export interface KeywordAnalysis {
  density: number;
  missingKeywords: string[];
  overusedKeywords: string[];
  keywordDistribution: { [key: string]: number };
}

/**
 * Analyzes ATS compliance of a resume
 * @param resumeText The resume text to analyze
 * @param jobData Job data for keyword matching
 * @returns Comprehensive ATS compliance report
 */
export function analyzeATSCompliance(resumeText: string, jobData: any): ATSComplianceReport {
  const sections = {
    formatting: analyzeFormatting(resumeText),
    keywords: analyzeKeywords(resumeText, jobData),
    structure: analyzeStructure(resumeText),
    content: analyzeContent(resumeText)
  };

  const overallScore = Math.round(
    (sections.formatting * 0.3) +
    (sections.keywords * 0.3) +
    (sections.structure * 0.2) +
    (sections.content * 0.2)
  );

  const recommendations = generateRecommendations(sections, resumeText, jobData);
  const warnings = generateWarnings(sections, resumeText);
  const strengths = generateStrengths(sections);

  return {
    overallScore,
    sections,
    recommendations,
    warnings,
    strengths
  };
}

/**
 * Analyzes formatting compliance
 * @param resumeText The resume text to analyze
 * @returns Formatting score (0-100)
 */
function analyzeFormatting(resumeText: string): number {
  let score = 0;

  // Check for ATS-friendly formatting
  const hasContactInfo = /(email|phone|address|linkedin)/i.test(resumeText);
  const hasSections = /(experience|education|skills|summary)/i.test(resumeText);
  const hasBulletPoints = /[ΓÇó\-\*]\s/.test(resumeText);
  const hasConsistentFormatting = /^[A-Z\s]+$/m.test(resumeText.split('\n')[0]);

  // Check for ATS-unfriendly elements
  const hasImages = /!\[.*\]\(.*\)/.test(resumeText);
  const hasTables = /\|.*\|/.test(resumeText);
  const hasComplexFormatting = /<[^>]+>/.test(resumeText);
  const hasGraphics = /[ΓûêΓûôΓûÆΓûæ]/g.test(resumeText);

  // Calculate base score
  if (hasContactInfo) score += 20;
  if (hasSections) score += 20;
  if (hasBulletPoints) score += 15;
  if (hasConsistentFormatting) score += 15;

  // Deduct for ATS-unfriendly elements
  if (hasImages) score -= 20;
  if (hasTables) score -= 15;
  if (hasComplexFormatting) score -= 10;
  if (hasGraphics) score -= 10;

  // Check for proper line length (not too long)
  const lines = resumeText.split('\n');
  const longLines = lines.filter(line => line.length > 80).length;
  const lineLengthScore = Math.max(0, 20 - (longLines / lines.length) * 20);
  score += lineLengthScore;

  return Math.min(100, Math.max(0, score));
}

/**
 * Analyzes keyword optimization
 * @param resumeText The resume text to analyze
 * @param jobData Job data containing required keywords
 * @returns Keyword score (0-100)
 */
function analyzeKeywords(resumeText: string, jobData: any): number {
  const requiredSkills = jobData.skills || [];
  const jobDescription = jobData.description || '';
  
  if (requiredSkills.length === 0) return 75; // Default if no skills provided

  const resumeLower = resumeText.toLowerCase();
  let matchedSkills = 0;

  // Check for exact skill matches
  requiredSkills.forEach(skill => {
    const skillLower = skill.toLowerCase();
    if (resumeLower.includes(skillLower)) {
      matchedSkills++;
    } else {
      // Check for variations
      const variations = generateSkillVariations(skill);
      if (variations.some(variation => resumeLower.includes(variation.toLowerCase()))) {
        matchedSkills++;
      }
    }
  });

  // Calculate percentage match
  const matchPercentage = (matchedSkills / requiredSkills.length) * 100;

  // Extract keywords from job description
  const jobKeywords = extractKeywordsFromDescription(jobDescription);
  let keywordMatches = 0;

  jobKeywords.forEach(keyword => {
    if (resumeLower.includes(keyword.toLowerCase())) {
      keywordMatches++;
    }
  });

  const keywordPercentage = jobKeywords.length > 0 ? (keywordMatches / jobKeywords.length) * 100 : 0;

  // Combine skill and keyword scores
  const finalScore = (matchPercentage * 0.7) + (keywordPercentage * 0.3);

  return Math.min(100, Math.max(0, finalScore));
}

/**
 * Analyzes resume structure
 * @param resumeText The resume text to analyze
 * @returns Structure score (0-100)
 */
function analyzeStructure(resumeText: string): number {
  let score = 0;

  // Check for proper section headers
  const sectionHeaders = resumeText.match(/^[A-Z\s]+$/gm) || [];
  if (sectionHeaders.length >= 3) score += 20;

  // Check for consistent bullet points
  const bulletPoints = resumeText.match(/^[ΓÇó\-\*]\s/gm) || [];
  if (bulletPoints.length >= 5) score += 20;

  // Check for proper date formatting
  const datePattern = /\b\d{1,2}\/\d{4}\s*[-ΓÇô]\s*\d{1,2}\/\d{4}\b|\b\d{4}\s*[-ΓÇô]\s*(present|current|now)\b/i;
  const hasProperDates = datePattern.test(resumeText);
  if (hasProperDates) score += 15;

  // Check for contact information format
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
  const phonePattern = /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/;
  if (emailPattern.test(resumeText)) score += 15;
  if (phonePattern.test(resumeText)) score += 10;

  // Check section order (ATS prefers specific order)
  const sectionOrder = ['contact', 'summary', 'experience', 'education', 'skills'];
  const resumeSections = resumeText.toLowerCase();
  let orderScore = 0;

  for (let i = 0; i < sectionOrder.length - 1; i++) {
    const currentSection = sectionOrder[i];
    const nextSection = sectionOrder[i + 1];
    const currentIndex = resumeSections.indexOf(currentSection);
    const nextIndex = resumeSections.indexOf(nextSection);

    if (currentIndex !== -1 && nextIndex !== -1 && currentIndex < nextIndex) {
      orderScore += 5;
    }
  }

  score += orderScore;

  return Math.min(100, Math.max(0, score));
}

/**
 * Analyzes content quality
 * @param resumeText The resume text to analyze
 * @returns Content score (0-100)
 */
function analyzeContent(resumeText: string): number {
  let score = 0;

  // Check for quantifiable achievements
  const metrics = (resumeText.match(/\d+%|\d+\+|\$\d+[KMB]?|\d+x|\d+\s*(years?|months?)/gi) || []).length;
  if (metrics >= 3) score += 25;
  else if (metrics >= 1) score += 15;

  // Check for action verbs
  const actionVerbs = ['achieved', 'improved', 'increased', 'developed', 'implemented', 'managed', 'led', 'created', 'designed', 'optimized'];
  const actionVerbCount = actionVerbs.filter(verb => resumeText.toLowerCase().includes(verb)).length;
  if (actionVerbCount >= 5) score += 20;
  else if (actionVerbCount >= 3) score += 15;
  else if (actionVerbCount >= 1) score += 10;

  // Check for section completeness
  const requiredSections = ['experience', 'education', 'skills'];
  const sectionCount = requiredSections.filter(section => 
    resumeText.toLowerCase().includes(section)
  ).length;
  if (sectionCount >= 3) score += 20;
  else if (sectionCount >= 2) score += 15;

  // Check for professional language
  const professionalTerms = ['collaborated', 'strategic', 'innovative', 'efficient', 'effective', 'comprehensive', 'proven', 'expertise'];
  const professionalTermCount = professionalTerms.filter(term => 
    resumeText.toLowerCase().includes(term)
  ).length;
  score += Math.min(20, professionalTermCount * 3);

  // Check for appropriate length
  const wordCount = resumeText.split(/\s+/).length;
  if (wordCount >= 300 && wordCount <= 800) score += 15; // Good length
  else if (wordCount >= 200 && wordCount <= 1000) score += 10; // Acceptable length

  return Math.min(100, Math.max(0, score));
}

/**
 * Generates recommendations based on analysis
 * @param sections Section scores
 * @param resumeText Resume text
 * @param jobData Job data
 * @returns Array of recommendations
 */
function generateRecommendations(sections: any, resumeText: string, jobData: any): string[] {
  const recommendations = [];

  if (sections.formatting < 70) {
    recommendations.push("Improve formatting by using consistent bullet points and section headers");
    recommendations.push("Remove any images, tables, or complex formatting that may not be ATS-friendly");
  }

  if (sections.keywords < 70) {
    const missingSkills = getMissingSkills(resumeText, jobData);
    if (missingSkills.length > 0) {
      recommendations.push(`Consider adding these relevant skills: ${missingSkills.slice(0, 3).join(', ')}`);
    }
    recommendations.push("Increase keyword density by naturally incorporating job-relevant terms");
  }

  if (sections.structure < 70) {
    recommendations.push("Ensure proper section order: Contact, Summary, Experience, Education, Skills");
    recommendations.push("Use consistent date formatting (MM/YYYY - MM/YYYY)");
  }

  if (sections.content < 70) {
    recommendations.push("Add more quantifiable achievements with specific numbers and percentages");
    recommendations.push("Use more action verbs to describe your accomplishments");
  }

  return recommendations;
}

/**
 * Generates warnings based on analysis
 * @param sections Section scores
 * @param resumeText Resume text
 * @returns Array of warnings
 */
function generateWarnings(sections: any, resumeText: string): string[] {
  const warnings = [];

  if (sections.formatting < 50) {
    warnings.push("ΓÜá∩╕Å Resume formatting may not be ATS-compatible");
  }

  if (sections.keywords < 50) {
    warnings.push("ΓÜá∩╕Å Low keyword match with job requirements");
  }

  if (sections.structure < 50) {
    warnings.push("ΓÜá∩╕Å Resume structure needs improvement for ATS scanning");
  }

  if (sections.content < 50) {
    warnings.push("ΓÜá∩╕Å Content quality needs enhancement");
  }

  // Check for specific issues
  if (resumeText.length < 200) {
    warnings.push("ΓÜá∩╕Å Resume is too short - consider adding more detail");
  }

  if (resumeText.length > 1200) {
    warnings.push("ΓÜá∩╕Å Resume is too long - consider condensing content");
  }

  return warnings;
}

/**
 * Generates strengths based on analysis
 * @param sections Section scores
 * @returns Array of strengths
 */
function generateStrengths(sections: any): string[] {
  const strengths = [];

  if (sections.formatting >= 80) {
    strengths.push("Γ£à Excellent ATS-compatible formatting");
  }

  if (sections.keywords >= 80) {
    strengths.push("Γ£à Strong keyword optimization");
  }

  if (sections.structure >= 80) {
    strengths.push("Γ£à Well-structured resume layout");
  }

  if (sections.content >= 80) {
    strengths.push("Γ£à High-quality content with quantifiable achievements");
  }

  return strengths;
}

/**
 * Gets missing skills from job requirements
 * @param resumeText Resume text
 * @param jobData Job data
 * @returns Array of missing skills
 */
function getMissingSkills(resumeText: string, jobData: any): string[] {
  const requiredSkills = jobData.skills || [];
  const resumeLower = resumeText.toLowerCase();
  
  return requiredSkills.filter(skill => {
    const skillLower = skill.toLowerCase();
    if (resumeLower.includes(skillLower)) return false;
    
    const variations = generateSkillVariations(skill);
    return !variations.some(variation => resumeLower.includes(variation.toLowerCase()));
  });
}

/**
 * Generates skill variations for better matching
 * @param skill The skill to generate variations for
 * @returns Array of skill variations
 */
function generateSkillVariations(skill: string): string[] {
  const variations = [skill];
  const skillLower = skill.toLowerCase();
  
  const skillMap: { [key: string]: string[] } = {
    'javascript': ['js', 'ecmascript', 'es6', 'es2015'],
    'python': ['py', 'python3'],
    'react': ['reactjs', 'react.js'],
    'node.js': ['nodejs', 'node'],
    'aws': ['amazon web services'],
    'sql': ['mysql', 'postgresql', 'sqlite'],
    'git': ['github', 'gitlab', 'version control'],
    'docker': ['containers', 'containerization'],
    'kubernetes': ['k8s', 'orchestration'],
    'machine learning': ['ml', 'ai', 'artificial intelligence']
  };
  
  if (skillMap[skillLower]) {
    variations.push(...skillMap[skillLower]);
  }
  
  return variations;
}

/**
 * Extracts keywords from job description
 * @param description Job description text
 * @returns Array of extracted keywords
 */
function extractKeywordsFromDescription(description: string): string[] {
  const words = description.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3);
  
  const stopWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'among', 'within', 'without', 'against', 'toward', 'towards', 'upon', 'across', 'behind', 'beyond', 'plus', 'except', 'but', 'until', 'unless', 'while', 'whereas', 'although', 'though', 'because', 'since', 'if', 'when', 'where', 'why', 'how', 'what', 'which', 'who', 'whom', 'whose', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'her', 'its', 'our', 'their', 'mine', 'yours', 'hers', 'ours', 'theirs', 'myself', 'yourself', 'himself', 'herself', 'itself', 'ourselves', 'yourselves', 'themselves', 'am', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'having', 'do', 'does', 'did', 'doing', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'shall', 'ought', 'need', 'dare', 'used'];
  
  const filteredWords = words.filter(word => !stopWords.includes(word));
  
  return [...new Set(filteredWords)].slice(0, 20);
}

/**
 * Analyzes keyword density and distribution
 * @param resumeText Resume text
 * @param jobData Job data
 * @returns Keyword analysis
 */
export function analyzeKeywordDensity(resumeText: string, jobData: any): KeywordAnalysis {
  const requiredSkills = jobData.skills || [];
  const resumeLower = resumeText.toLowerCase();
  const totalWords = resumeText.split(/\s+/).length;
  
  const keywordDistribution: { [key: string]: number } = {};
  const missingKeywords: string[] = [];
  const overusedKeywords: string[] = [];
  
  // Analyze each required skill
  requiredSkills.forEach(skill => {
    const skillLower = skill.toLowerCase();
    const variations = generateSkillVariations(skill);
    let found = false;
    let count = 0;
    
    // Check for exact matches and variations
    variations.forEach(variation => {
      const regex = new RegExp(`\\b${variation.toLowerCase()}\\b`, 'g');
      const matches = resumeLower.match(regex);
      if (matches) {
        count += matches.length;
        found = true;
      }
    });
    
    if (found) {
      keywordDistribution[skill] = count;
      const density = (count / totalWords) * 100;
      if (density > 2) { // More than 2% density
        overusedKeywords.push(skill);
      }
    } else {
      missingKeywords.push(skill);
    }
  });
  
  const totalKeywords = Object.values(keywordDistribution).reduce((sum, count) => sum + count, 0);
  const density = (totalKeywords / totalWords) * 100;
  
  return {
    density,
    missingKeywords,
    overusedKeywords,
    keywordDistribution
  };
}
